import { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import {
  Target,
  TrendingUp,
  TrendingDown,
  Users,
  Trophy,
  Award,
  Medal,
  Star,
  Plus,
  Edit,
  Trash2,
  RefreshCw,
  Calendar,
  DollarSign,
  FileText,
  UserPlus,
  Percent,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  CheckCircle,
  XCircle,
  Clock,
  Loader2,
  BarChart3,
  PieChart,
  Settings,
  Eye
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const TIPOS_META = [
  { value: "CONTRATOS", label: "Quantidade de Contratos", icon: FileText, unidade: "contratos" },
  { value: "VALOR_CONTRATOS", label: "Valor de Contratos", icon: DollarSign, unidade: "R$" },
  { value: "COMISSAO", label: "Valor de Comissão", icon: DollarSign, unidade: "R$" },
  { value: "CLIENTES", label: "Clientes Captados", icon: UserPlus, unidade: "clientes" },
  { value: "CONVERSAO", label: "Taxa de Conversão", icon: Percent, unidade: "%" },
];

const PERIODOS_META = [
  { value: "DIARIO", label: "Diário" },
  { value: "SEMANAL", label: "Semanal" },
  { value: "MENSAL", label: "Mensal" },
  { value: "TRIMESTRAL", label: "Trimestral" },
  { value: "ANUAL", label: "Anual" },
];

const NIVEIS_META = [
  { value: "INDIVIDUAL", label: "Individual", icon: Users },
  { value: "EQUIPE", label: "Equipe", icon: Users },
  { value: "EMPRESA", label: "Empresa", icon: Target },
];

const STATUS_CORES = {
  "SUPERADA": { bg: "bg-purple-100", text: "text-purple-700", border: "border-purple-300", icon: Trophy },
  "ATINGIDA": { bg: "bg-green-100", text: "text-green-700", border: "border-green-300", icon: CheckCircle },
  "EM_ANDAMENTO": { bg: "bg-blue-100", text: "text-blue-700", border: "border-blue-300", icon: Clock },
  "NAO_ATINGIDA": { bg: "bg-red-100", text: "text-red-700", border: "border-red-300", icon: XCircle },
  "SEM_DADOS": { bg: "bg-gray-100", text: "text-gray-700", border: "border-gray-300", icon: Clock },
};

const formatCurrency = (value) => {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value || 0);
};

const formatNumber = (value) => {
  return new Intl.NumberFormat("pt-BR").format(value || 0);
};

// ==================== COMPONENTES ====================

function MetaCard({ meta, onEdit, onDelete, onViewDetails }) {
  const tipoInfo = TIPOS_META.find(t => t.value === meta.tipo);
  const TipoIcon = tipoInfo?.icon || Target;
  const statusInfo = STATUS_CORES[meta.status_progresso] || STATUS_CORES["SEM_DADOS"];
  const StatusIcon = statusInfo.icon;
  
  const valorFormatado = meta.tipo === "VALOR_CONTRATOS" || meta.tipo === "COMISSAO" 
    ? formatCurrency(meta.valor_realizado)
    : formatNumber(meta.valor_realizado);
  
  const metaFormatada = meta.tipo === "VALOR_CONTRATOS" || meta.tipo === "COMISSAO"
    ? formatCurrency(meta.valor_meta)
    : formatNumber(meta.valor_meta);

  return (
    <Card 
      data-testid={`meta-card-${meta.id}`}
      className={cn(
        "hover:shadow-lg transition-all cursor-pointer",
        statusInfo.border,
        "border-l-4"
      )}
    >
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <div className={cn("p-2 rounded-lg", statusInfo.bg)}>
              <TipoIcon className={cn("h-4 w-4", statusInfo.text)} />
            </div>
            <div>
              <CardTitle className="text-base">{meta.nome}</CardTitle>
              <CardDescription className="text-xs">
                {NIVEIS_META.find(n => n.value === meta.nivel)?.label} • {PERIODOS_META.find(p => p.value === meta.periodo)?.label}
              </CardDescription>
            </div>
          </div>
          <Badge className={cn(statusInfo.bg, statusInfo.text, "border", statusInfo.border)}>
            <StatusIcon className="h-3 w-3 mr-1" />
            {meta.status_progresso?.replace("_", " ")}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="pt-2">
        <div className="space-y-3">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-500">Progresso</span>
            <span className="font-semibold">{valorFormatado} / {metaFormatada}</span>
          </div>
          
          <div className="relative">
            <Progress 
              value={Math.min(meta.percentual_atingido || 0, 100)} 
              className={cn(
                "h-3",
                meta.percentual_atingido >= 100 ? "[&>div]:bg-green-500" : 
                meta.percentual_atingido >= 80 ? "[&>div]:bg-blue-500" : 
                meta.percentual_atingido >= 50 ? "[&>div]:bg-amber-500" : "[&>div]:bg-red-500"
              )}
            />
            {meta.valor_minimo && (
              <div 
                className="absolute top-0 h-3 w-0.5 bg-amber-600" 
                style={{ left: `${(meta.valor_minimo / meta.valor_meta) * 100}%` }}
                title={`Mínimo: ${meta.valor_minimo}`}
              />
            )}
            {meta.valor_otimo && (
              <div 
                className="absolute top-0 h-3 w-0.5 bg-purple-600" 
                style={{ left: `${Math.min((meta.valor_otimo / meta.valor_meta) * 100, 100)}%` }}
                title={`Ótimo: ${meta.valor_otimo}`}
              />
            )}
          </div>
          
          <div className="flex items-center justify-between">
            <span className={cn("text-lg font-bold", statusInfo.text)}>
              {(meta.percentual_atingido || 0).toFixed(1)}%
            </span>
            <div className="flex gap-1">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => onViewDetails(meta)}>
                      <Eye className="h-3.5 w-3.5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Ver detalhes</TooltipContent>
                </Tooltip>
              </TooltipProvider>
              {onEdit && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => onEdit(meta)}>
                        <Edit className="h-3.5 w-3.5" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Editar</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
              {onDelete && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-600 hover:text-red-700" onClick={() => onDelete(meta)}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Excluir</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function RankingCard({ ranking, posicao }) {
  const getMedal = (pos) => {
    if (pos === 0) return { icon: Trophy, color: "text-yellow-500", bg: "bg-yellow-100" };
    if (pos === 1) return { icon: Medal, color: "text-gray-400", bg: "bg-gray-100" };
    if (pos === 2) return { icon: Award, color: "text-amber-600", bg: "bg-amber-100" };
    return { icon: Star, color: "text-blue-500", bg: "bg-blue-50" };
  };

  const medal = getMedal(posicao);
  const MedalIcon = medal.icon;
  const detalhes = ranking.detalhes || [];
  const totalMetas = ranking.total_metas || detalhes.length || 0;

  const getColorClass = (pct) =>
    pct >= 100 ? "text-green-600" :
    pct >= 80 ? "text-blue-600" :
    pct >= 50 ? "text-amber-600" : "text-red-600";

  const getBarColor = (pct) =>
    pct >= 100 ? "bg-green-500" :
    pct >= 80 ? "bg-blue-500" :
    pct >= 50 ? "bg-amber-500" : "bg-red-500";

  const formatValue = (val, tipo) => {
    if (tipo === "VALOR_CONTRATOS" || tipo === "COMISSAO") {
      return "R$ " + Number(val || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 });
    }
    if (tipo === "CONVERSAO") return Number(val || 0).toFixed(1) + "%";
    return String(val || 0);
  };

  return (
    <div
      data-testid={`ranking-item-${posicao}`}
      className={cn(
        "rounded-lg transition-all border",
        posicao < 3 ? medal.bg + " border-transparent" : "bg-gray-50 hover:bg-gray-100 border-gray-200"
      )}
    >
      {/* Header */}
      <div className="flex items-center gap-3 p-3 pb-1">
        <div className={cn("flex items-center justify-center w-8 h-8 rounded-full shrink-0", medal.bg)}>
          {posicao < 3 ? (
            <MedalIcon className={cn("h-5 w-5", medal.color)} />
          ) : (
            <span className="font-bold text-gray-600">{posicao + 1}</span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm truncate">{ranking.nome}</p>
        </div>
        <div className="text-right shrink-0">
          <p className={cn("text-lg font-bold", getColorClass(ranking.media_percentual))}>
            {ranking.media_percentual.toFixed(1)}%
          </p>
          <p className="text-[10px] text-gray-500">{ranking.metas_atingidas}/{totalMetas} atingidas</p>
        </div>
      </div>

      {/* Barra geral */}
      <div className="px-3 pb-2">
        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
          <div
            className={cn("h-full rounded-full transition-all duration-500", getBarColor(ranking.media_percentual))}
            style={{ width: `${Math.min(ranking.media_percentual, 100)}%` }}
          />
        </div>
      </div>

      {/* Detalhes de cada meta - SEMPRE VISIVEL */}
      {detalhes.length > 0 && (
        <div className="px-3 pb-3 space-y-2" data-testid={`ranking-details-${posicao}`}>
          {detalhes.map((d, i) => {
            const pct = d.percentual_atingido || 0;
            const falta = d.percentual_faltante || 0;
            return (
              <div key={i} className="bg-white/80 rounded-md p-2 space-y-1">
                <div className="flex items-center justify-between gap-1">
                  <span className="text-[11px] font-medium text-gray-700 truncate">{d.nome_meta}</span>
                  <Badge variant="outline" className="text-[9px] shrink-0 px-1 py-0">
                    {d.tipo?.replace("_", " ")}
                  </Badge>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className={cn("h-full rounded-full transition-all duration-500", getBarColor(pct))}
                      style={{ width: `${Math.min(pct, 100)}%` }}
                    />
                  </div>
                  <span className={cn("text-[11px] font-bold whitespace-nowrap", getColorClass(pct))}>
                    {pct.toFixed(1)}%
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-1 text-[10px]">
                  <div className="text-center bg-blue-50 rounded px-1 py-0.5">
                    <div className="text-gray-500">Atingido</div>
                    <div className="font-bold text-gray-800">{formatValue(d.valor_realizado, d.tipo)}</div>
                  </div>
                  <div className="text-center bg-gray-50 rounded px-1 py-0.5">
                    <div className="text-gray-500">Meta</div>
                    <div className="font-bold text-gray-800">{formatValue(d.valor_meta, d.tipo)}</div>
                  </div>
                  <div className={cn("text-center rounded px-1 py-0.5", falta > 0 ? "bg-red-50" : "bg-green-50")}>
                    <div className="text-gray-500">Falta</div>
                    <div className={cn("font-bold", falta > 0 ? "text-red-600" : "text-green-600")}>{falta.toFixed(1)}%</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function MetaFormDialog({ open, onClose, meta, usuarios, equipes, onSave }) {
  const [formData, setFormData] = useState({
    nome: "",
    descricao: "",
    tipo: "CONTRATOS",
    periodo: "MENSAL",
    nivel: "INDIVIDUAL",
    valor_meta: "",
    valor_minimo: "",
    valor_otimo: "",
    data_inicio: new Date().toISOString().split("T")[0],
    data_fim: "",
    usuario_id: "",
    equipe_ids: [],
    bonus_atingimento: "",
    bonus_superacao: "",
    ativo: true,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (meta) {
      setFormData({
        nome: meta.nome || "",
        descricao: meta.descricao || "",
        tipo: meta.tipo || "CONTRATOS",
        periodo: meta.periodo || "MENSAL",
        nivel: meta.nivel || "INDIVIDUAL",
        valor_meta: meta.valor_meta || "",
        valor_minimo: meta.valor_minimo || "",
        valor_otimo: meta.valor_otimo || "",
        data_inicio: meta.data_inicio || new Date().toISOString().split("T")[0],
        data_fim: meta.data_fim || "",
        usuario_id: meta.usuario_id || "",
        equipe_ids: meta.equipe_ids || [],
        bonus_atingimento: meta.bonus_atingimento || "",
        bonus_superacao: meta.bonus_superacao || "",
        ativo: meta.ativo !== false,
      });
    } else {
      // Calcular fim do mês atual
      const hoje = new Date();
      const ultimoDia = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0);
      setFormData(prev => ({
        ...prev,
        nome: "",
        descricao: "",
        valor_meta: "",
        valor_minimo: "",
        valor_otimo: "",
        usuario_id: "",
        equipe_ids: [],
        data_inicio: hoje.toISOString().split("T")[0],
        data_fim: ultimoDia.toISOString().split("T")[0],
      }));
    }
  }, [meta, open]);

  const handleSubmit = async () => {
    if (!formData.nome || !formData.valor_meta) {
      toast.error("Preencha o nome e o valor da meta");
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        ...formData,
        valor_meta: parseFloat(formData.valor_meta) || 0,
        valor_minimo: formData.valor_minimo ? parseFloat(formData.valor_minimo) : null,
        valor_otimo: formData.valor_otimo ? parseFloat(formData.valor_otimo) : null,
        bonus_atingimento: formData.bonus_atingimento ? parseFloat(formData.bonus_atingimento) : null,
        bonus_superacao: formData.bonus_superacao ? parseFloat(formData.bonus_superacao) : null,
      };
      
      if (meta?.id) {
        await axios.put(`${API}/api/metas/${meta.id}`, payload);
        toast.success("Meta atualizada com sucesso!");
      } else {
        await axios.post(`${API}/api/metas`, payload);
        toast.success("Meta criada com sucesso!");
      }
      
      onSave();
      onClose();
    } catch (error) {
      console.error("Erro ao salvar meta:", error);
      toast.error("Erro ao salvar meta");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Target className="h-5 w-5" />
            {meta?.id ? "Editar Meta" : "Nova Meta"}
          </DialogTitle>
          <DialogDescription>
            Configure os parâmetros da meta para acompanhamento de performance.
          </DialogDescription>
        </DialogHeader>
        
        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label htmlFor="nome">Nome da Meta *</Label>
              <Input
                id="nome"
                data-testid="meta-nome-input"
                value={formData.nome}
                onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                placeholder="Ex: Meta de Contratos Janeiro"
              />
            </div>
            
            <div className="col-span-2">
              <Label htmlFor="descricao">Descrição</Label>
              <Input
                id="descricao"
                value={formData.descricao}
                onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                placeholder="Descrição opcional da meta"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label>Tipo de Meta *</Label>
              <Select value={formData.tipo} onValueChange={(v) => setFormData({ ...formData, tipo: v })}>
                <SelectTrigger data-testid="meta-tipo-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIPOS_META.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Período *</Label>
              <Select value={formData.periodo} onValueChange={(v) => setFormData({ ...formData, periodo: v })}>
                <SelectTrigger data-testid="meta-periodo-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PERIODOS_META.map(p => (
                    <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Nível *</Label>
              <Select value={formData.nivel} onValueChange={(v) => setFormData({ ...formData, nivel: v })}>
                <SelectTrigger data-testid="meta-nivel-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {NIVEIS_META.map(n => (
                    <SelectItem key={n.value} value={n.value}>{n.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label htmlFor="valor_meta">Valor da Meta *</Label>
              <Input
                id="valor_meta"
                data-testid="meta-valor-input"
                type="number"
                value={formData.valor_meta}
                onChange={(e) => setFormData({ ...formData, valor_meta: e.target.value })}
                placeholder="Ex: 50"
              />
            </div>
            
            <div>
              <Label htmlFor="valor_minimo">Meta Mínima (Bronze)</Label>
              <Input
                id="valor_minimo"
                type="number"
                value={formData.valor_minimo}
                onChange={(e) => setFormData({ ...formData, valor_minimo: e.target.value })}
                placeholder="Ex: 30"
              />
            </div>
            
            <div>
              <Label htmlFor="valor_otimo">Meta Ótima (Ouro)</Label>
              <Input
                id="valor_otimo"
                type="number"
                value={formData.valor_otimo}
                onChange={(e) => setFormData({ ...formData, valor_otimo: e.target.value })}
                placeholder="Ex: 70"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="data_inicio">Data Início *</Label>
              <Input
                id="data_inicio"
                type="date"
                value={formData.data_inicio}
                onChange={(e) => setFormData({ ...formData, data_inicio: e.target.value })}
              />
            </div>
            
            <div>
              <Label htmlFor="data_fim">Data Fim *</Label>
              <Input
                id="data_fim"
                type="date"
                value={formData.data_fim}
                onChange={(e) => setFormData({ ...formData, data_fim: e.target.value })}
              />
            </div>
          </div>
          
          {formData.nivel === "INDIVIDUAL" && (
            <div>
              <Label>Funcionário</Label>
              <Select value={formData.usuario_id} onValueChange={(v) => setFormData({ ...formData, usuario_id: v })}>
                <SelectTrigger data-testid="meta-usuario-select">
                  <SelectValue placeholder="Selecione um funcionário" />
                </SelectTrigger>
                <SelectContent>
                  {usuarios.map(u => (
                    <SelectItem key={u.id} value={u.id}>{u.nome}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="bonus_atingimento">Bônus Atingimento (R$)</Label>
              <Input
                id="bonus_atingimento"
                type="number"
                value={formData.bonus_atingimento}
                onChange={(e) => setFormData({ ...formData, bonus_atingimento: e.target.value })}
                placeholder="Ex: 500"
              />
            </div>
            
            <div>
              <Label htmlFor="bonus_superacao">Bônus Superação (%)</Label>
              <Input
                id="bonus_superacao"
                type="number"
                value={formData.bonus_superacao}
                onChange={(e) => setFormData({ ...formData, bonus_superacao: e.target.value })}
                placeholder="Ex: 10"
              />
            </div>
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={handleSubmit} disabled={saving} data-testid="salvar-meta-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            {meta?.id ? "Salvar Alterações" : "Criar Meta"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ResumoCard({ titulo, valor, subtitulo, icon: Icon, cor, trend }) {
  return (
    <Card data-testid={`resumo-card-${titulo.toLowerCase().replace(/\s/g, "-")}`}>
      <CardContent className="pt-4">
        <div className="flex items-center justify-between">
          <div className={cn("p-2 rounded-lg", cor)}>
            <Icon className="h-5 w-5 text-white" />
          </div>
          {trend !== undefined && (
            <div className={cn(
              "flex items-center text-xs",
              trend >= 0 ? "text-green-600" : "text-red-600"
            )}>
              {trend >= 0 ? <TrendingUp className="h-3 w-3 mr-1" /> : <TrendingDown className="h-3 w-3 mr-1" />}
              {Math.abs(trend)}%
            </div>
          )}
        </div>
        <div className="mt-3">
          <p className="text-2xl font-bold">{valor}</p>
          <p className="text-sm text-gray-500">{titulo}</p>
          {subtitulo && <p className="text-xs text-gray-400">{subtitulo}</p>}
        </div>
      </CardContent>
    </Card>
  );
}


// ==================== COMPONENTE PRINCIPAL ====================

export default function PlanoMetas() {
  const [loading, setLoading] = useState(true);
  const [metas, setMetas] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [equipes, setEquipes] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [periodoRef, setPeriodoRef] = useState(() => {
    const hoje = new Date();
    return `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, "0")}`;
  });
  
  const [showMetaForm, setShowMetaForm] = useState(false);
  const [metaEditando, setMetaEditando] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [metaDeletando, setMetaDeletando] = useState(null);
  const [filtroNivel, setFiltroNivel] = useState("all");
  const [filtroTipo, setFiltroTipo] = useState("all");

  // Obter informações do usuário logado
  const usuario = JSON.parse(localStorage.getItem("usuario") || "{}");
  const empresa = JSON.parse(localStorage.getItem("empresa") || "{}");
  const isAtendenteOuCorretor = usuario?.perfil === "ATENDENTE" || usuario?.perfil === "CORRETOR";
  const podeGerenciarMetas = usuario?.perfil === "MASTER_GERAL" || usuario?.perfil === "ADMIN_EMPRESA";

  useEffect(() => {
    carregarDados();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodoRef]);

  const carregarDados = async () => {
    setLoading(true);
    try {
      // Parâmetros de filtro baseados no perfil
      const metasParams = { ativo: true };
      
      // Se for ATENDENTE ou CORRETOR, buscar apenas suas metas e da empresa
      if (isAtendenteOuCorretor && usuario?.id) {
        metasParams.usuario_id = usuario.id;
      }
      
      const [dashboardRes, metasRes, usuariosRes, equipesRes] = await Promise.all([
        axios.get(`${API}/api/metas/dashboard/resumo`, { params: { periodo_ref: periodoRef } }),
        axios.get(`${API}/api/metas`, { params: metasParams }),
        axios.get(`${API}/api/usuarios-sistema`),
        axios.get(`${API}/api/equipes`),
      ]);
      
      setDashboard(dashboardRes.data);
      
      // Para ATENDENTE/CORRETOR: mostrar suas metas individuais + metas da empresa
      let todasMetas = dashboardRes.data.metas || metasRes.data.metas || [];
      
      if (isAtendenteOuCorretor && usuario?.id) {
        // Buscar também metas da empresa
        try {
          const metasEmpresaRes = await axios.get(`${API}/api/metas`, { 
            params: { ativo: true, nivel: "EMPRESA" } 
          });
          const metasEmpresa = metasEmpresaRes.data.metas || [];
          
          // Combinar: metas individuais do usuário + metas da empresa (sem duplicatas)
          const metasIndividuais = todasMetas.filter(m => m.usuario_id === usuario.id || m.nivel === "INDIVIDUAL" && m.usuario_id === usuario.id);
          const idsExistentes = new Set(metasIndividuais.map(m => m.id));
          const metasEmpresaNaoDuplicadas = metasEmpresa.filter(m => !idsExistentes.has(m.id));
          
          todasMetas = [...metasIndividuais, ...metasEmpresaNaoDuplicadas];
        } catch (e) {
          console.error("Erro ao buscar metas da empresa:", e);
        }
      }
      
      setMetas(todasMetas);
      setUsuarios(usuariosRes.data.usuarios || []);
      setEquipes(equipesRes.data.equipes || []);
    } catch (error) {
      console.error("Erro ao carregar dados:", error);
      toast.error("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  };

  const handleEditMeta = (meta) => {
    setMetaEditando(meta);
    setShowMetaForm(true);
  };

  const handleDeleteMeta = async () => {
    if (!metaDeletando) return;
    
    try {
      await axios.delete(`${API}/api/metas/${metaDeletando.id}`);
      toast.success("Meta excluída com sucesso!");
      carregarDados();
    } catch (error) {
      console.error("Erro ao excluir meta:", error);
      toast.error("Erro ao excluir meta");
    } finally {
      setShowDeleteConfirm(false);
      setMetaDeletando(null);
    }
  };

  const handleCalcularProgresso = async (meta) => {
    try {
      await axios.post(`${API}/api/metas/${meta.id}/calcular-progresso`, null, {
        params: { periodo_ref: periodoRef }
      });
      toast.success("Progresso calculado!");
      carregarDados();
    } catch (error) {
      console.error("Erro ao calcular progresso:", error);
      toast.error("Erro ao calcular progresso");
    }
  };

  const metasFiltradas = metas.filter(m => {
    if (filtroNivel !== "all" && m.nivel !== filtroNivel) return false;
    if (filtroTipo !== "all" && m.tipo !== filtroTipo) return false;
    return true;
  });

  const resumo = dashboard?.resumo || {};
  const ranking = dashboard?.ranking || [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="loading-spinner">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="plano-metas-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Target className="h-6 w-6" />
            Plano de Metas
          </h1>
          <p className="text-gray-500">
            {isAtendenteOuCorretor 
              ? `Acompanhe suas metas e da empresa - ${usuario?.nome || "Usuário"}`
              : "Acompanhe o desempenho e gerencie metas da equipe"}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Input
            type="month"
            value={periodoRef}
            onChange={(e) => setPeriodoRef(e.target.value)}
            className="w-40"
            data-testid="periodo-input"
          />
          <Button onClick={carregarDados} variant="outline" size="sm" data-testid="refresh-btn">
            <RefreshCw className="h-4 w-4 mr-2" />
            Atualizar
          </Button>
          {podeGerenciarMetas && (
            <Button onClick={() => { setMetaEditando(null); setShowMetaForm(true); }} data-testid="nova-meta-btn">
              <Plus className="h-4 w-4 mr-2" />
              Nova Meta
            </Button>
          )}
        </div>
      </div>

      {/* Cards de Resumo */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <ResumoCard
          titulo="Total de Metas"
          valor={resumo.total_metas || 0}
          icon={Target}
          cor="bg-blue-500"
        />
        <ResumoCard
          titulo="Metas Atingidas"
          valor={resumo.atingidas || 0}
          subtitulo={`${resumo.superadas || 0} superadas`}
          icon={CheckCircle}
          cor="bg-green-500"
        />
        <ResumoCard
          titulo="Em Andamento"
          valor={resumo.em_andamento || 0}
          icon={Clock}
          cor="bg-amber-500"
        />
        <ResumoCard
          titulo="Não Atingidas"
          valor={resumo.nao_atingidas || 0}
          icon={XCircle}
          cor="bg-red-500"
        />
      </div>

      {/* Conteúdo Principal */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Lista de Metas */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" />
                  Metas do Período
                </CardTitle>
                <div className="flex gap-2">
                  <Select value={filtroNivel} onValueChange={setFiltroNivel}>
                    <SelectTrigger className="w-32" data-testid="filtro-nivel">
                      <SelectValue placeholder="Nível" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos Níveis</SelectItem>
                      {NIVEIS_META.map(n => (
                        <SelectItem key={n.value} value={n.value}>{n.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={filtroTipo} onValueChange={setFiltroTipo}>
                    <SelectTrigger className="w-40" data-testid="filtro-tipo">
                      <SelectValue placeholder="Tipo" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos Tipos</SelectItem>
                      {TIPOS_META.map(t => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {metasFiltradas.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Target className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p className="text-lg font-medium">Nenhuma meta encontrada</p>
                  {isAtendenteOuCorretor ? (
                    <>
                      <p className="text-sm">Você ainda não possui metas cadastradas.</p>
                      <p className="text-sm mt-1">Aguarde seu gestor configurar suas metas individuais ou da empresa.</p>
                    </>
                  ) : (
                    <>
                      <p className="text-sm">Crie uma nova meta para começar a acompanhar</p>
                      <Button className="mt-4" onClick={() => setShowMetaForm(true)}>
                        <Plus className="h-4 w-4 mr-2" />
                        Criar Meta
                      </Button>
                    </>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {metasFiltradas.map(meta => (
                    <MetaCard
                      key={meta.id}
                      meta={meta}
                      onEdit={podeGerenciarMetas ? handleEditMeta : null}
                      onDelete={podeGerenciarMetas ? (m) => { setMetaDeletando(m); setShowDeleteConfirm(true); } : null}
                      onViewDetails={handleCalcularProgresso}
                    />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Ranking */}
        <div>
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Trophy className="h-5 w-5 text-yellow-500" />
                Ranking de Performance
              </CardTitle>
              <CardDescription>Top performers do período</CardDescription>
            </CardHeader>
            <CardContent>
              {ranking.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Trophy className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Nenhum ranking disponível</p>
                </div>
              ) : (
                <ScrollArea className="h-[400px]">
                  <div className="space-y-2">
                    {ranking.map((r, idx) => (
                      <RankingCard key={r.usuario_id} ranking={r} posicao={idx} />
                    ))}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Dialog de Formulário */}
      <MetaFormDialog
        open={showMetaForm}
        onClose={() => { setShowMetaForm(false); setMetaEditando(null); }}
        meta={metaEditando}
        usuarios={usuarios}
        equipes={equipes}
        onSave={carregarDados}
      />

      {/* Dialog de Confirmação de Exclusão */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar Exclusão</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja excluir a meta "{metaDeletando?.nome}"? Esta ação não pode ser desfeita.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>Cancelar</Button>
            <Button variant="destructive" onClick={handleDeleteMeta} data-testid="confirmar-delete-btn">
              Excluir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
